// EditInspection — row-click edit drawer. The page fetches the inspection
// detail and threads it in; the draft seeds from it once per open (seededRef,
// so cache refetches never clobber user edits).
//
// Saving always goes through PATCH /inspections/update/:id — one path. The four
// structural fields (form/project/companyLevel/companyNode) are sent only while
// the inspection is a draft, where they stay editable; on anything else they are
// locked (disabled), omitted from the payload, and deliberately NOT validated,
// so stale data can't wedge the form.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Drawer } from '@/components/common';
import {
  useUpdateInspectionMutation,
  type InspectionDetail,
} from '@/api/endpoints/inspectionApi';
import InspectionFormFields, {
  emptyInspectionDraft,
  isWindowInvalid,
  useInspectionFormData,
  type InspectionDraft,
} from './InspectionFormFields';

interface EditInspectionProps {
  open: boolean;
  /** Fetched by the page — null while loading (footer stays disabled). */
  detail: InspectionDetail | null;
  loading?: boolean;
  onClose: () => void;
}

const EditInspection = ({ open, detail, loading = false, onClose }: EditInspectionProps) => {
  const { t } = useTranslation();

  const [draft, setDraft] = useState(emptyInspectionDraft);
  const [submitted, setSubmitted] = useState(false);

  const data = useInspectionFormData(open, draft.companyLevel);
  const [updateInspection, { isLoading: updating }] = useUpdateInspectionMutation();

  const patch = (changes: Partial<InspectionDraft>) => setDraft((d) => ({ ...d, ...changes }));

  // Reset on open; the detail then seeds the draft once per open below.
  const seededRef = useRef(false);
  useEffect(() => {
    if (open) {
      setDraft(emptyInspectionDraft);
      setSubmitted(false);
      seededRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !detail || seededRef.current) return;
    seededRef.current = true;
    setDraft({
      form: detail.form?.id ?? null,
      project: detail.project?.id ?? null,
      title: detail.title,
      description: detail.description,
      priority: detail.priority || 'medium',
      tagIds: detail.tags.map((tag) => tag.id),
      // Native date inputs take YYYY-MM-DD.
      startDate: detail.startDate.slice(0, 10),
      endDate: detail.endDate.slice(0, 10),
      companyLevel: detail.companyLevel?.id ?? null,
      companyNode: detail.companyNode?.id ?? null,
      location: detail.location,
      coordinates: detail.coordinates,
      autoFill: detail.autoFill,
    });
  }, [open, detail]);

  // The inspection may reference entities missing from the selection endpoints
  // (unpublished form, inaccessible project/level/node) — append them from the
  // detail's populated titles/icons so the selects show real labels, not placeholders.
  const pickerData = useMemo(() => {
    if (!detail) return data;
    const { forms, projects, levels, nodes, inspectionTags } = data;
    const formRef = detail.form;
    const projectRef = detail.project;
    const levelRef = detail.companyLevel;
    const nodeRef = detail.companyNode;
    return {
      inspectionTags,
      forms:
        formRef && !forms.some((f) => f.id === formRef.id)
          ? [...forms, { id: formRef.id, name: formRef.title, icon: formRef.icon }]
          : forms,
      projects:
        projectRef && !projects.some((p) => p.id === projectRef.id)
          ? [
              ...projects,
              { id: projectRef.id, name: projectRef.title, projectType: '', icon: projectRef.icon },
            ]
          : projects,
      levels:
        levelRef && !levels.some((l) => l.id === levelRef.id)
          ? [...levels, { id: levelRef.id, name: levelRef.name, level: levelRef.level, icon: levelRef.icon }]
          : levels,
      nodes:
        nodeRef && !nodes.some((n) => n.id === nodeRef.id)
          ? [...nodes, { id: nodeRef.id, name: nodeRef.name }]
          : nodes,
    };
  }, [data, detail]);

  // Structural fields stay editable only while the inspection is a draft;
  // locked while loading too, so the selects never flash enabled.
  const isDraftState = detail?.state === 'draft';
  const locked = !detail || !isDraftState;

  // Drafts require only the name; non-drafts additionally check the window
  // order. Locked fields are never validated (they can't be fixed here).
  const titleMissing = !draft.title.trim();
  const invalid = titleMissing || (!isDraftState && isWindowInvalid(draft));

  const saveChanges = async () => {
    if (!detail) return;
    setSubmitted(true);
    if (invalid) return;
    try {
      await updateInspection({
        id: detail.id,
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        priority: draft.priority,
        tags: draft.tagIds.length ? draft.tagIds : undefined,
        startDate: draft.startDate || undefined,
        endDate: draft.endDate || undefined,
        location: draft.location.trim() || undefined,
        coordinates: draft.coordinates.trim() || undefined,
        autoFill: draft.autoFill,
        // Structural fields stay editable — and are only accepted — on drafts.
        ...(isDraftState && {
          form: draft.form || undefined,
          project: draft.project || undefined,
          companyLevel: draft.companyLevel || undefined,
          companyNode: draft.companyNode ? [draft.companyNode] : undefined,
        }),
      }).unwrap();
      toast.success(t('inspection_updated', { name: draft.title.trim() }));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      icon='clipboard-pen'
      title={t('inspection_edit_title')}
      subtitle={t('inspection_edit_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={updating}>
            {t('button_cancel')}
          </Button>
          <Button
            iconStart='file-pen'
            onClick={saveChanges}
            loading={updating}
            disabled={loading || !detail}
          >
            {t('inspection_save_changes')}
          </Button>
        </>
      }
    >
      {/* No structural required-errors here: drafts only require the name, and
          on non-drafts those fields are locked (and excluded from the PATCH). */}
      <InspectionFormFields
        draft={draft}
        patch={patch}
        data={pickerData}
        locked={locked}
        showRequiredErrors={false}
        showTitleError={submitted && titleMissing}
        viewAssignees
      />
    </Drawer>
  );
};

export default EditInspection;
